import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Radio,
  RadioGroup,
  Input,
  Checkbox,
  Button,
  Textarea,
  useToast,
  Divider,
  List,
  ListItem,
  IconButton,
  Flex,
  Badge,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
} from '@chakra-ui/react';
import { SearchIcon, QuestionIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { BsEmojiSmile, BsSave, BsSend, BsPlayCircle, BsArchive } from 'react-icons/bs';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { AiOutlineFileText } from 'react-icons/ai';
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import { MdAdd } from 'react-icons/md';
import EmojiPicker from 'emoji-picker-react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { format } from 'date-fns';
import MessagesHelpModal from './MessagesHelpModal';
import MessageTemplates from './MessageTemplates';
import MessageHistory from './MessageHistory';
import { substituteVariables, getAvailableVariables } from '../utils/messageUtils';

const Messages = ({ members: propMembers = [] }) => {
  const supabase = useSupabaseClient();
  const session = useSession();
  const toast = useToast();

  const messageRef = useRef(null);

  // Initialize state
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [debouncedMessage, setDebouncedMessage] = useState('');
  const [sendType, setSendType] = useState('individual');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [messageHistoryKey, setMessageHistoryKey] = useState(0);
  const [currentSession, setCurrentSession] = useState(null);
  const [clubName, setClubName] = useState('');

  // Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [focusedTemplateId, setFocusedTemplateId] = useState(null);
  const [originalMessage, setOriginalMessage] = useState('');
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Modal disclosures
  const { isOpen: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
  const { isOpen: isChangeTemplateModalOpen, onOpen: onChangeTemplateModalOpen, onClose: onChangeTemplateModalClose } = useDisclosure();
  const { isOpen: isHelpOpen, onOpen: onHelpOpen, onClose: onHelpClose } = useDisclosure();
  const { isOpen: isTemplatesOpen, onOpen: onTemplatesOpen, onClose: onTemplatesClose } = useDisclosure();

  // State for sending status
  const [isSending, setIsSending] = useState(false);

  // State for date format menu
  const [dateFormatMenu, setDateFormatMenu] = useState({
    isOpen: false,
    anchorEl: null
  });

  // State for help content
  const [availableVariables] = useState(getAvailableVariables());

  // State for template filtering and sorting
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('template_name');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentSession(session);
    };
    getSession();
  }, [supabase.auth]);

  // Load club preferences
  useEffect(() => {
    const loadClubPreferences = async () => {
      try {
        const { data, error } = await supabase
          .from('club_preferences')
          .select('club_name')
          .single();

        if (error) throw error;
        setClubName(data.club_name);
      } catch (error) {
        console.error('Error loading club preferences:', error);
        toast({
          title: 'Error loading club preferences',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    loadClubPreferences();
  }, []);

  // Load members on component mount
  useEffect(() => {
    const loadInitialMembers = async () => {
      try {
        const { data, error } = await supabase
          .from('texting_contacts')
          .select('id, first_name, last_name, full_name, phone_number, email')
          .eq('is_rotary_member', true)
          .eq('consent_status', 'CONSENTED')
          .order('first_name');

        if (error) throw error;

        // Format the members for display
        const formattedData = data?.map(contact => ({
          id: contact.id,
          first_name: contact.first_name,
          last_name: contact.last_name,
          full_name: contact.full_name,
          name: contact.full_name,  // For display in the UI
          phone_number: contact.phone_number,
          email: contact.email
        })) || [];

        console.log('[loadInitialMembers] Loaded members:', formattedData);
        setMembers(formattedData);
      } catch (error) {
        console.error('Error loading members:', error);
        toast({
          title: 'Error loading members',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    loadInitialMembers();
  }, []);

  // Load groups
  useEffect(() => {
    const fetchGroups = async () => {
      const { data, error } = await supabase
        .from('member_groups')
        .select('*');

      if (error) {
        console.error('Error loading groups:', error);
        toast({
          title: 'Error loading groups',
          description: error.message,
          status: 'error',
          duration: 5000,
        });
      } else {
        console.log('Loaded groups:', data);
        setGroups(data || []);
      }
    };

    fetchGroups();
  }, []);

  // Load group members when a group is selected
  const loadGroupMembers = async (group) => {
    try {
      console.log('Loading members for group:', group);
      
      // If "All Members" is selected, use all active members
      if (group.name === 'All Members') {
        const activeMembers = members.map(member => ({
          id: member.id,
          first_name: member.first_name,
          last_name: member.last_name,
          full_name: member.full_name,
          name: member.name,
          phone_number: member.phone_number,
          email: member.email
        }));
        console.log('All active members loaded:', activeMembers);
        setFilteredMembers(activeMembers);
        setSelectedMembers(activeMembers);
        return;
      }

      // Otherwise, load specific group members
      const { data: groupMembers, error } = await supabase
        .from('member_groups_members')
        .select(`
          texting_contacts (
            id,
            first_name,
            last_name,
            full_name,
            phone_number,
            email,
            consent_status
          )
        `)
        .eq('group_id', group.id);

      if (error) throw error;

      // Extract member objects and filter for active consent
      const activeMembers = groupMembers
        .map(gm => gm.texting_contacts)
        .filter(member => member.consent_status === 'CONSENTED')
        .map(member => ({
          id: member.id,
          first_name: member.first_name,
          last_name: member.last_name,
          full_name: member.full_name || `${member.first_name} ${member.last_name}`.trim(),
          name: `${member.first_name} ${member.last_name}`.trim(),
          phone_number: member.phone_number,
          email: member.email
        }));

      console.log('Group members loaded:', activeMembers);
      setFilteredMembers(activeMembers);
      setSelectedMembers(activeMembers); // Automatically select all active group members
    } catch (error) {
      console.error('Error loading group members:', error);
      toast({
        title: 'Error loading group members',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Effect to load group members when a group is selected
  useEffect(() => {
    if (selectedGroup) {
      loadGroupMembers(selectedGroup);
    }
  }, [selectedGroup]);

  // Load templates from database
  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .order('template_name');

      if (error) throw error;

      // Add "Blank" template at the beginning
      const blankTemplate = {
        id: 'blank',
        template_name: 'Blank',
        content: '',
        template_type: 'sms'
      };
      
      // Filter out any "Blank" templates from database results
      const filteredData = data.filter(template => 
        template.template_name.toLowerCase() !== 'blank'
      );
      
      setTemplates([blankTemplate, ...filteredData]);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Error loading templates',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Load templates on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      await loadTemplates();
      // Set Blank template as default selected
      const blankTemplate = {
        id: 'blank',
        template_name: 'Blank',
        content: '',
        template_type: 'sms'
      };
      setSelectedTemplate(blankTemplate);
    };
    
    loadInitialData();
  }, []);

  // Debounce message updates
  useEffect(() => {
    console.log('Debounced message updated:', debouncedMessage);
    const timer = setTimeout(() => {
      setMessage(debouncedMessage);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [debouncedMessage]);

  // Filter members based on search
  const filteredMembersList = members.filter(member => 
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  console.log('Filtered members:', filteredMembersList);

  // Filter and sort groups with All Members at top
  const filteredGroupsList = groups
    .filter(group => group.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      // Always put "All Members" at the top
      if (a.name === 'All Members') return -1;
      if (b.name === 'All Members') return 1;
      // Sort the rest alphabetically
      return a.name.localeCompare(b.name);
    });

  // Update the filtered templates list to search both name and content
  const filteredTemplatesList = templates
    .filter(template =>
      template.template_name.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
      (template.content && template.content.toLowerCase().includes(templateSearchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      // Always keep Blank template at top
      if (a.template_name === 'Blank') return -1;
      if (b.template_name === 'Blank') return 1;

      // Sort by the selected field
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';

      // Special handling for dates
      if (sortField === 'updated_at') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else {
        // Case-insensitive string comparison for text fields
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      // Apply sort direction
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Function to substitute variables in message text
  const handleSendTest = async () => {
    try {
      if (!session?.access_token) {
        console.error('[handleSendTest] No session token found');
        throw new Error('Not authenticated. Please log in again.');
      }

      if (!message || message.trim().length === 0) {
        toast({
          title: 'No message content',
          description: 'Please enter a message to send.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Get the logged-in user's profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (profileError) {
        console.error('[handleSendTest] Error fetching user profile:', profileError);
        throw new Error('Could not fetch user profile');
      }

      if (!profile.phone_number) {
        throw new Error('No phone number found in your profile. Please update your profile with a phone number to use test send.');
      }

      const cleanPhone = profile.phone_number.replace(/^\+/, '');
      const { substituted: substitutedMessage, substitutionValues } = substituteVariables(
        message,
        {
          first_name: profile.first_name,
          last_name: profile.last_name,
          full_name: profile.full_name || `${profile.first_name} ${profile.last_name}`,
          phone_number: profile.phone_number,
          email: profile.email
        },
        { clubName }
      );
      
      // Add [TEST] prefix to the message
      const testMessage = `[TEST] ${substitutedMessage}`;
      
      const requestBody = {
        recipients: [profile.phone_number],
        recipientIds: {
          [cleanPhone]: profile.id
        },
        messages: {
          [profile.phone_number]: testMessage
        },
        template: message,
        substitutionValues: {
          [profile.phone_number]: substitutionValues
        },
        isTest: true
      };

      console.log('[handleSendTest] Sending test message:', {
        recipient: profile.phone_number,
        recipientId: profile.id,
        cleanPhone,
        recipientIds: requestBody.recipientIds,
        originalMessage: message,
        testMessage
      });

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(requestBody)
      });

      const responseData = await response.json();
      console.log('[handleSendTest] Response:', {
        status: response.status,
        data: responseData
      });

      if (!response.ok) {
        throw new Error(responseData.error || responseData.details || 'Failed to send test message');
      }

      toast({
        title: 'Test message sent!',
        description: 'A test message has been sent to your phone number.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('[handleSendTest] Error:', error);
      toast({
        title: 'Error sending test message',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSendNow = async () => {
    try {
      setIsSending(true);

      if (selectedMembers.length === 0) {
        toast({
          title: 'No recipients selected',
          description: 'Please select at least one recipient.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (!message || message.trim().length === 0) {
        toast({
          title: 'No message content',
          description: 'Please enter a message to send.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (!session?.access_token) {
        throw new Error('Not authenticated. Please log in again.');
      }

      console.log('[handleSendNow] Starting message send:', {
        selectedMembers,
        message,
        clubName,
        session
      });

      // Create a mapping of phone numbers to member IDs
      const recipientIds = {};
      const recipients = [];
      const recipientMessages = {};
      const substitutionValues = {};

      // Process each member and prepare their personalized message
      for (const member of selectedMembers) {
        console.log('[handleSendNow] Processing member:', member);
        const substitutionResult = substituteVariables(message, member, { clubName });
        console.log('[handleSendNow] Substituted message:', {
          original: message,
          substituted: substitutionResult.substituted,
          member: member
        });

        // Add to our recipients list and ID mapping
        const cleanPhone = member.phone_number.replace(/^\+/, '');
        recipientIds[cleanPhone] = member.id;
        recipients.push(member.phone_number);

        // Store the substituted message and values for this recipient
        recipientMessages[member.phone_number] = substitutionResult.substituted;
        substitutionValues[member.phone_number] = substitutionResult.substitutionValues;
      }

      console.log('[handleSendNow] Prepared request:', {
        recipients,
        recipientIds,
        messages: recipientMessages,
        substitutionValues,
        sendType,
        selectedGroup
      });

      const requestBody = {
        recipients,
        recipientIds,
        messages: recipientMessages,
        template: message,  // Original template
        substitutionValues,  // Values used for each recipient
        isTest: false,
        group_id: sendType === 'group' && selectedGroup ? selectedGroup.id : null  // Add group_id when sending to group
      };

      console.log('[handleSendNow] Request body:', requestBody);

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(requestBody)
      });
      const data = await response.json();
      console.log('[handleSendNow] Response:', { 
        status: response.status, 
        data,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      if (!response.ok) {
        const errorMessage = data.error || data.details || data.message || 'Failed to send message';
        console.error('[handleSendNow] Error details:', { data, errorMessage });
        throw new Error(errorMessage);
      }

      // Show appropriate toast based on results
      if (data.sent < data.total) {
        const failedCount = data.total - data.sent;
        toast({
          title: 'Partial Success',
          description: `Message sent to ${data.sent} out of ${data.total} recipient(s)`,
          status: 'warning',
          duration: 7000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Message sent successfully',
          description: `Message sent to ${data.total} out of ${data.total} recipient(s)`,
          status: 'success',
          duration: 5000,
        });
      }

      // Clear the form and reset state
      setMessage('');
      setSelectedMembers([]);
      setSelectedGroup(null);
      
      // Clear the textarea by resetting its value
      const textarea = document.querySelector('textarea[name="message"]');
      if (textarea) {
        textarea.value = '';
      }

      setIsSending(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error sending message',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
      setIsSending(false);
    }
  };

  const handleMessageChange = (e) => {
    console.log('Message changed:', e.target.value);
    setDebouncedMessage(e.target.value);
  };

  const handleKeyDown = (e) => {
    // Handle keyboard shortcuts here if needed
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (canSend) {
            handleSendNow();
          }
          break;
        default:
          break;
      }
    }
  };

  // Compute if send button should be enabled based on send type
  const canSend = sendType === 'individual' 
    ? selectedMembers.length > 0 && message.trim().length > 0
    : selectedGroup && message.trim().length > 0;

  const handleMemberSelect = (member) => {
    console.log('[handleMemberSelect] Adding member:', member);
    setSelectedMembers([...selectedMembers, member]);
  };

  const handleRemoveMember = (member) => {
    console.log('[handleRemoveMember] Removing member:', member);
    setSelectedMembers(selectedMembers.filter(m => m.id !== member.id));
  };

  const onEmojiClick = (emojiObject) => {
    setMessage(prev => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  const handleTemplateSelect = (template) => {
    console.log('Template selected:', template);
    
    if (template.template_name === 'Blank') {
      console.log('Blank template selected, clearing message');
      setSelectedTemplate(template);
      setDebouncedMessage('');
      setMessage('');
      setOriginalMessage('');
      return;
    }

    // If there are unsaved changes, show confirmation modal
    if (message && message !== originalMessage && selectedTemplate) {
      setPendingTemplate(template);
      onChangeTemplateModalOpen();
      return;
    }

    // Otherwise, apply the template directly
    setSelectedTemplate(template);
    setDebouncedMessage(template.content || '');
    setMessage(template.content || '');
    setOriginalMessage(template.content || '');
  };

  const handleTemplateKeyDown = (e, template) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleTemplateSelect(template);
        break;
      case 'ArrowDown':
        e.preventDefault();
        const nextTemplate = filteredTemplatesList[
          filteredTemplatesList.findIndex(t => t.id === template.id) + 1
        ];
        if (nextTemplate) setFocusedTemplateId(nextTemplate.id);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevTemplate = filteredTemplatesList[
          filteredTemplatesList.findIndex(t => t.id === template.id) - 1
        ];
        if (prevTemplate) setFocusedTemplateId(prevTemplate.id);
        break;
    }
  };

  const insertVariable = (variable) => {
    if (variable === 'Send Date') {
      // Show date format menu with examples
      const now = new Date();
      const options = [
        {
          label: `Full - ${format(now, 'EEEE, MMMM dd, yyyy')}`,
          value: 'sendDate:full'
        },
        {
          label: `Long - ${format(now, 'MMMM dd, yyyy')}`,
          value: 'sendDate:long'
        },
        {
          label: `Medium - ${format(now, 'MMM dd, yyyy')}`,
          value: 'sendDate:medium'
        },
        {
          label: `Short - ${format(now, 'M/d/yyyy')}`,
          value: 'sendDate:short'
        }
      ];

      return (
        <Menu>
          <MenuButton as={Button} size="sm" variant="outline" rightIcon={<ChevronDownIcon />}>
            Send Date
          </MenuButton>
          <MenuList>
            {options.map((option) => (
              <MenuItem 
                key={option.value}
                onClick={() => {
                  const textarea = document.getElementById('messageTextarea');
                  const cursorPos = textarea.selectionStart;
                  const textBefore = debouncedMessage.substring(0, cursorPos);
                  const textAfter = debouncedMessage.substring(textarea.selectionEnd);
                  const variableText = `{{${option.value}}}`;
                  
                  const newText = `${textBefore}${variableText}${textAfter}`;
                  setDebouncedMessage(newText);
                  
                  // Set cursor position after the inserted variable
                  setTimeout(() => {
                    textarea.focus();
                    const newCursorPos = cursorPos + variableText.length;
                    textarea.setSelectionRange(newCursorPos, newCursorPos);
                  }, 0);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      );
    }

    if (variable === 'Phone') {
      const samplePhone = '+18887779999';
      const options = [
        {
          label: `Unformatted long - ${samplePhone}`,
          value: 'phone:unformattedLong'
        },
        {
          label: `Unformatted short - ${samplePhone.slice(-10)}`,
          value: 'phone:unformattedShort'
        },
        {
          label: `Formatted long - +1 (888) 777-9999`,
          value: 'phone:formattedLong'
        },
        {
          label: `Formatted short - (888) 777-9999`,
          value: 'phone:formattedShort'
        }
      ];

      return (
        <Menu>
          <MenuButton as={Button} size="sm" variant="outline" rightIcon={<ChevronDownIcon />}>
            Phone
          </MenuButton>
          <MenuList>
            {options.map((option) => (
              <MenuItem 
                key={option.value}
                onClick={() => {
                  const textarea = document.getElementById('messageTextarea');
                  const cursorPos = textarea.selectionStart;
                  const textBefore = debouncedMessage.substring(0, cursorPos);
                  const textAfter = debouncedMessage.substring(textarea.selectionEnd);
                  const variableText = `{{${option.value}}}`;
                  
                  const newText = `${textBefore}${variableText}${textAfter}`;
                  setDebouncedMessage(newText);
                  
                  // Set cursor position after the inserted variable
                  setTimeout(() => {
                    textarea.focus();
                    const newCursorPos = cursorPos + variableText.length;
                    textarea.setSelectionRange(newCursorPos, newCursorPos);
                  }, 0);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      );
    }

    // For other variables, insert them directly
    const variableMap = {
      'First Name': 'firstName',
      'Last Name': 'lastName',
      'Full Name': 'fullName',
      'Email': 'email',
      'Club Name': 'clubName'
    };
    const variableText = `{{${variableMap[variable]}}}`;
    const textarea = document.getElementById('messageTextarea');
    const cursorPos = textarea.selectionStart;
    const textBefore = debouncedMessage.substring(0, cursorPos);
    const textAfter = debouncedMessage.substring(textarea.selectionEnd);
    
    const newText = `${textBefore}${variableText}${textAfter}`;
    setDebouncedMessage(newText);
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = cursorPos + variableText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleTemplateChangeConfirm = () => {
    if (!pendingTemplate) return;
    
    setSelectedTemplate(pendingTemplate);
    setDebouncedMessage(pendingTemplate.content || '');
    setMessage(pendingTemplate.content || '');
    setOriginalMessage(pendingTemplate.content || '');
    setPendingTemplate(null);
    onChangeTemplateModalClose();
  };

  const handleChangeTemplateConfirm = async (action) => {
    console.log('Template change action:', action);
    
    // If trying to update Blank template, force save as new
    if (action === 'update' && selectedTemplate?.template_name === 'Blank') {
      action = 'new';
    }

    switch (action) {
      case 'update':
        await handleSaveTemplate();
        // Fall through to switch template after saving
      case 'discard':
        try {
          // If discarding changes, reload the original template content
          const { data, error } = await supabase
            .from('message_templates')
            .select('*')
            .eq('id', pendingTemplate.id)
            .single();

          if (error) throw error;

          console.log('Reloaded template content:', data);
          setSelectedTemplate(data);
          setDebouncedMessage(data.content || '');
          setMessage(data.content || '');
          setOriginalMessage(data.content || '');
        } catch (error) {
          console.error('Error reloading template:', error);
          toast({
            title: 'Error loading template',
            description: error.message,
            status: 'error',
            duration: 5000,
          });
        }
        setPendingTemplate(null);
        break;
      case 'new':
        setIsNewTemplate(true);
        setTemplateName('');
        onSaveModalOpen();
        break;
    }
    onChangeTemplateModalClose();
  };

  const handleSaveAsTemplate = () => {
    if (!message.trim()) {
      toast({
        title: 'Error',
        description: 'Message cannot be empty',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Always show "Save as New Template" dialog for Blank template
    if (selectedTemplate?.template_name === 'Blank') {
      setIsNewTemplate(true);
      setTemplateName('');
      onSaveModalOpen();
      return;
    }

    // For existing templates, show update/new options
    if (selectedTemplate) {
      setIsNewTemplate(false);
      setTemplateName(selectedTemplate.template_name);
    } else {
      setIsNewTemplate(true);
      setTemplateName('');
    }
    onSaveModalOpen();
  };

  const handleSaveTemplate = async () => {
    try {
      if (isNewTemplate || selectedTemplate?.template_name === 'Blank') {
        // Check if template name already exists
        const { data, error: checkError } = await supabase
          .from('message_templates')
          .select('template_name')
          .ilike('template_name', templateName);

        if (checkError) throw checkError;

        if (data && data.length > 0) {
          toast({
            title: 'Template name already exists',
            description: 'Please choose a different name for your template.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        // Create new template
        const { error } = await supabase
          .from('message_templates')
          .insert({
            template_name: templateName,
            content: message,
            template_type: 'sms',
            is_active: true,
            version: 1,
            created_by: (await supabase.auth.getUser()).data.user.id,
            updated_by: (await supabase.auth.getUser()).data.user.id,
          });

        if (error) throw error;

        toast({
          title: 'Template Created',
          status: 'success',
          duration: 3000,
        });
      } else {
        // Update existing template
        const { error } = await supabase
          .from('message_templates')
          .update({
            content: message,
            version: selectedTemplate.version + 1,
            updated_at: new Date().toISOString(),
            updated_by: (await supabase.auth.getUser()).data.user.id,
          })
          .eq('id', selectedTemplate.id);

        if (error) throw error;

        toast({
          title: 'Template Updated',
          status: 'success',
          duration: 3000,
        });
      }

      // Refresh templates and update states
      await loadTemplates();
      setOriginalMessage(message);
      onSaveModalClose();
    } catch (error) {
      console.error('Error saving template:', error);
      // Handle specific error cases
      if (error.code === '23505') {
        toast({
          title: 'Template name already exists',
          description: 'Please choose a different name for your template.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Error saving template',
          description: 'An unexpected error occurred. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  useEffect(() => {
    if (selectedGroup) {
      loadGroupMembers(selectedGroup);
    }
  }, [selectedGroup]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleArchiveTemplate = async (template) => {
    try {
      const { error } = await supabase
        .from('message_templates')
        .update({
          archived_at: new Date().toISOString(),
        })
        .eq('id', template.id);

      if (error) throw error;

      toast({
        title: 'Template Archived',
        status: 'success',
        duration: 3000,
      });

      await loadTemplates();
    } catch (error) {
      console.error('Error archiving template:', error);
      toast({
        title: 'Error archiving template',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box width="100%" p={4}>
      {/* Page Header */}
      <HStack justify="space-between" mb={6}>
        <Text fontSize="2xl" fontWeight="bold">Send Messages</Text>
        <IconButton
          icon={<QuestionIcon />}
          aria-label="Help"
          variant="ghost"
          onClick={() => setIsHelpModalOpen(true)}
        />
      </HStack>

      <HStack spacing={8} align="flex-start">
        {/* Left Column - Member Selection */}
        <VStack spacing={1} align="stretch" width="300px">
          {/* Send Type Selection - Aligned with Templates header */}
          <HStack spacing={4} mb={0}>
            <Radio
              value="individual"
              isChecked={sendType === 'individual'}
              onChange={(e) => setSendType('individual')}
            >
              Send to Members
            </Radio>
            <Radio
              value="group"
              isChecked={sendType === 'group'}
              onChange={(e) => setSendType('group')}
            >
              Send to Group
            </Radio>
          </HStack>

          {/* Help Text */}
          <Text fontSize="sm" color="gray.600" height="28px" mb={0}>
            {sendType === 'individual' ? 'Select one or more members' : 'Select a group to message'}
          </Text>

          {/* Search Input - Changes placeholder based on send type */}
          <InputGroup size="md" mb={1}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder={sendType === 'individual' ? "Search members..." : "Search groups..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="md"
              fontSize="md"
            />
          </InputGroup>

          {/* Members/Groups List Box */}
          <Box maxH="300px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
            {sendType === 'individual' ? (
              <List spacing={1}>
                {filteredMembersList.map((member) => (
                  <ListItem key={member.id}>
                    <Checkbox
                      isChecked={selectedMembers.some(m => m.id === member.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMembers([...selectedMembers, member]);
                        } else {
                          setSelectedMembers(selectedMembers.filter(m => m.id !== member.id));
                        }
                      }}
                    >
                      {member.name} ({member.phone_number})
                    </Checkbox>
                  </ListItem>
                ))}
              </List>
            ) : (
              <List spacing={1}>
                {filteredGroupsList.map((group) => (
                  <ListItem key={group.id}>
                    <Radio
                      value={group.id}
                      isChecked={selectedGroup?.id === group.id}
                      onChange={() => setSelectedGroup(group)}
                    >
                      {group.name}
                    </Radio>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Selected Group Members Box - Only shown in group mode */}
          {sendType === 'group' && selectedGroup && (
            <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Selected Group Members ({selectedMembers.length})
              </Text>
              <Box maxH="150px" overflowY="auto">
                <List spacing={0}>
                  {selectedMembers.map((member) => (
                    <ListItem key={member.id}>
                      <Text fontSize="sm">
                        {member.name} ({member.phone_number})
                      </Text>
                    </ListItem>
                  ))}
                  {selectedMembers.length === 0 && (
                    <ListItem>
                      <Text fontSize="sm" color="gray.500">
                        No active members in this group
                      </Text>
                    </ListItem>
                  )}
                </List>
              </Box>
            </Box>
          )}
        </VStack>

        {/* Right Column - Message Composition */}
        <VStack spacing={4} align="stretch" flex={1}>
          {/* Templates Section */}
          <Box>
            <HStack justify="space-between" mb={2}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={1}>Templates</Text>
                <Text fontSize="sm" color="gray.600">
                  Select a template for your text message
                </Text>
              </Box>
            </HStack>
            <InputGroup size="md" mb={2}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search by template name, content..."
                value={templateSearchQuery}
                onChange={(e) => setTemplateSearchQuery(e.target.value)}
                size="md"
                fontSize="md"
              />
              {templateSearchQuery && (
                <InputRightElement>
                  <IconButton
                    icon={<CloseIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={() => setTemplateSearchQuery('')}
                    aria-label="Clear search"
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <Box 
              borderWidth="1px" 
              borderRadius="md" 
              maxHeight="150px" 
              overflowY="auto"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  },
                },
              }}
            >
              <Table variant="simple" size="sm" layout="fixed" width="100%">
                <colgroup>
                  <col width="250px" />
                  <col />
                  <col width="160px" />
                </colgroup>
                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                  <Tr>
                    <Th 
                      width="250px"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      cursor="pointer"
                      onClick={() => {
                        if (sortField === 'template_name') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('template_name');
                          setSortDirection('asc');
                        }
                      }}
                    >
                      Name
                      <Icon
                        as={sortField === 'template_name'
                          ? (sortDirection === 'asc' ? FaSortUp : FaSortDown)
                          : FaSort}
                        ml={2}
                        color={sortField === 'template_name' ? 'inherit' : 'gray.400'}
                      />
                    </Th>
                    <Th
                      cursor="pointer"
                      onClick={() => {
                        if (sortField === 'content') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('content');
                          setSortDirection('asc');
                        }
                      }}
                    >
                      Content
                      <Icon
                        as={sortField === 'content'
                          ? (sortDirection === 'asc' ? FaSortUp : FaSortDown)
                          : FaSort}
                        ml={2}
                        color={sortField === 'content' ? 'inherit' : 'gray.400'}
                      />
                    </Th>
                    <Th 
                      width="160px"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      textAlign="right"
                      pr={4}
                      cursor="pointer"
                      onClick={() => {
                        if (sortField === 'updated_at') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('updated_at');
                          setSortDirection('asc');
                        }
                      }}
                    >
                      Last Modified
                      <Icon
                        as={sortField === 'updated_at'
                          ? (sortDirection === 'asc' ? FaSortUp : FaSortDown)
                          : FaSort}
                        ml={2}
                        color={sortField === 'updated_at' ? 'inherit' : 'gray.400'}
                      />
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(!templateSearchQuery || 'blank'.includes(templateSearchQuery.toLowerCase())) && (
                    <Tr
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => handleTemplateSelect({ template_name: 'Blank', content: '' })}
                      bg={selectedTemplate?.template_name === 'Blank' ? 'blue.200' : 'white'}
                    >
                      <Td 
                        fontWeight="bold"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        py={0.5}
                      >
                        Blank
                      </Td>
                      <Td py={0.5}>
                        <Box
                          maxWidth="100%"
                          overflowX="auto"
                          whiteSpace="nowrap"
                        >
                          {''}
                        </Box>
                      </Td>
                      <Td
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        textAlign="right"
                        pr={4}
                        py={0.5}
                      >
                        -
                      </Td>
                    </Tr>
                  )}
                  {filteredTemplatesList
                    .filter(template => !template.archived_at && template.template_name.toLowerCase() !== 'blank')
                    .map((template) => (
                    <Tr
                      key={template.id}
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                      bg={selectedTemplate?.id === template.id ? 'blue.200' : 'white'}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <Td 
                        fontWeight="bold"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        py={0.5}
                      >
                        {template.template_name}
                      </Td>
                      <Td py={0.5}>
                        <Box
                          maxWidth="100%"
                          overflowX="auto"
                          whiteSpace="nowrap"
                          css={{
                            '&::-webkit-scrollbar': {
                              height: '6px',
                              borderRadius: '4px',
                              backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                              },
                            },
                          }}
                        >
                          {template.content || '-'}
                        </Box>
                      </Td>
                      <Td
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        textAlign="right"
                        pr={4}
                        py={0.5}
                      >
                        {template.updated_at ? format(new Date(template.updated_at), 'MM/dd/yyyy HH:mm') : '-'}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* Variables Section */}
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={1}>Message Editor with Dynamic Variables</Text>
            <Text fontSize="sm" color="gray.600" mb={2}>
              Type your message directly or customize a template from above.
              <br />Add dynamic content by clicking any variable below - they'll update automatically when sent.
            </Text>
            <HStack spacing={2} wrap="wrap">
              {['First Name', 'Last Name', 'Full Name', 'Email', 'Club Name'].map((variable) => (
                <Button
                  key={variable}
                  size="sm"
                  variant="outline"
                  onClick={() => insertVariable(variable)}
                  isDisabled={variable === 'Club Name' && !clubName}
                >
                  {variable}
                </Button>
              ))}
              {insertVariable('Phone')}
              {insertVariable('Send Date')}
            </HStack>
          </Box>

          {/* Message Input */}
          <Box position="relative">
            <Textarea
              id="messageTextarea"
              placeholder="Type your message here..."
              value={debouncedMessage}
              onChange={handleMessageChange}
              rows={6}
              resize="vertical"
            />
            <IconButton
              icon={<BsEmojiSmile />}
              position="absolute"
              bottom={2}
              right={2}
              variant="ghost"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            />
            {showEmojiPicker && (
              <Box
                position="absolute"
                bottom="100%"
                right={0}
                zIndex={1}
              >
                <EmojiPicker onEmojiClick={onEmojiClick} />
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <HStack spacing={4} width="100%" justify="space-between">
            <HStack spacing={4}>
              <Button
                colorScheme="blue"
                onClick={handleSendNow}
                isLoading={isSending}
                loadingText="Sending..."
                isDisabled={!selectedMembers?.length && sendType === 'individual' || !selectedGroup && sendType === 'group' || !message}
              >
                Send Now
              </Button>
              <Button
                colorScheme="gray"
                onClick={handleSendTest}
                isLoading={isSending}
                loadingText="Testing..."
                isDisabled={!message}
              >
                Test Send
              </Button>
              <Button
                colorScheme="gray"
                onClick={() => {/* TODO: Implement Send Later */}}
                isDisabled={!selectedMembers?.length && sendType === 'individual' || !selectedGroup && sendType === 'group' || !message}
              >
                Send Later
              </Button>
            </HStack>
            <Button
              colorScheme="teal"
              variant="outline"
              onClick={onSaveModalOpen}
              isDisabled={!message}
            >
              Save Template
            </Button>
          </HStack>
        </VStack>
      </HStack>

      {/* Modals */}
      {/* Save Template Modal */}
      <Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Save Template
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedTemplate?.template_name === 'Blank' ? (
              <>
                <Text mb={4} color="gray.600">
                  The Blank template cannot be modified. Please save your message as a new template.
                </Text>
                <FormControl isRequired>
                  <FormLabel>Template Name</FormLabel>
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter template name"
                  />
                </FormControl>
              </>
            ) : selectedTemplate && !isNewTemplate ? (
              <VStack align="stretch" spacing={4}>
                <Text>
                  Would you like to update "{selectedTemplate.template_name}" or save as a new template?
                </Text>
                <ButtonGroup spacing={4}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsNewTemplate(true);
                      setTemplateName('');
                    }}
                  >
                    Save as New Template
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={handleSaveTemplate}
                  >
                    Update Current Template
                  </Button>
                </ButtonGroup>
              </VStack>
            ) : (
              <FormControl isRequired>
                <FormLabel>Template Name</FormLabel>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter template name"
                />
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            {(isNewTemplate || selectedTemplate?.template_name === 'Blank') && (
              <>
                <Button variant="ghost" mr={3} onClick={onSaveModalClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSaveTemplate}
                  isDisabled={!templateName.trim()}
                >
                  Save New Template
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Template Change Confirmation Modal */}
      <Modal isOpen={isChangeTemplateModalOpen} onClose={onChangeTemplateModalClose} size="xl">
        <ModalOverlay />
        <ModalContent minW="700px">
          <ModalHeader>Save Changes to Template?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              You have unsaved changes to the template "{selectedTemplate?.template_name}".
              What would you like to do?
            </Text>
          </ModalBody>
          <ModalFooter display="flex" justifyContent="flex-end" gap={4}>
            <Button 
              variant="ghost" 
              onClick={() => handleChangeTemplateConfirm('discard')}
              minW="140px"
            >
              Discard Changes
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleChangeTemplateConfirm('new')}
              minW="160px"
            >
              Save as New Template
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={() => handleChangeTemplateConfirm('update')}
              minW="180px"
              isDisabled={selectedTemplate?.template_name === 'Blank'}
            >
              Update Current Template
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Help Modal */}
      <MessagesHelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </Box>
  );
};

export default Messages;
